import { Avatar }              from 'antd';
import { Col }                 from 'antd';
import type { FormInstance }   from 'antd';
import { Form }                from 'antd';
import { AppFormComponent }    from 'components/App/AppForm';
import FormItem                from 'components/App/FormItem';
import FormattedMessageWrapper from 'components/App/FormattedMessageWrapper';
import Input                   from 'components/App/IntlInput';
import InputPassword           from 'components/App/IntlInputPassword';
import { observer }            from 'mobx-react';
import { RefObject }           from 'react';
import React                   from 'react';
import Select                  from 'components/App/IntlSelect';
import Row                     from 'components/App/Row';
import UserShowModel           from 'models/UserShowModel';
import handleApiError          from 'utils/handleApiError';
import handleFormError         from 'utils/handleFormError';
import { ApiCollection }       from 'collections/ApiCollection';
import YesOrNo                 from 'components/App/YesOrNo';
import { getRoleIntlLabel }    from 'constants/RoleReferences';
import { rolesBack }           from 'constants/RoleReferences';
import { rolesBackWithShows }  from 'constants/RoleReferences';
import RoleModel               from 'models/RoleModel';
import UserModel               from 'models/UserModel';
import ShowModel               from 'models/ShowModel';
import { messageSuccessIntl }  from 'utils/messageIntl';

interface AdministratorFormProps {
  model: UserModel;
  onSuccess?: () => void;
}

interface AdministratorFormState {
  displayShowSelect: boolean;
}

@observer
class AdministratorForm extends AppFormComponent<AdministratorFormProps> {
  public collRoleBack = new ApiCollection(RoleModel);
  public collShow = new ApiCollection(ShowModel);
  public collUserShow = new ApiCollection(UserShowModel);

  public formRef: RefObject<FormInstance> = React.createRef();

  public state: AdministratorFormState = {
    displayShowSelect: false,
  };

  public async componentDidMount() {
    await Promise.all([
      this.collRoleBack
        .setFilter('reference', rolesBack)
        .list(),

      this.collShow
        .setSort('createdAt', false)
        .list(),
    ]);

    await this.setInitialValues();
  }

  public render() {
    return (
      <Form
        ref={this.formRef}
      >
        <Row>
          <Col span={24}>
            <FormItem label="Utilisateur actif" name="enabled">
              <YesOrNo />
            </FormItem>
          </Col>
          <Col md={12} sm={24}>
            <FormItem label="Société" name="company">
              <Input placeholder="Société" />
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={24}>
            <FormItem label="Nom" name="lastName">
              <Input placeholder="Nom" />
            </FormItem>
          </Col>
          <Col md={12} sm={24}>
            <FormItem label="Prénom" name="firstName">
              <Input placeholder="Prénom" />
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={24}>
            <FormItem label="Email" name="email">
              <Input placeholder="Email" />
            </FormItem>
          </Col>
          <Col md={12} sm={24}>
            <FormItem label="Téléphone" name="phone" required={false}>
              <Input placeholder="Téléphone" />
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={24}>
            <FormItem label="Rôle" name="role">
              <Select
                onChange={(value?: string) => {
                  const role = this.collRoleBack.findBy('@id', value) as RoleModel;
                  this.setState({ displayShowSelect: !!rolesBackWithShows.includes(role?.reference), });
                }}
                options={this.collRoleBack.map((role: RoleModel) => ({
                  label: getRoleIntlLabel(role),
                  value: role.iri,
                }))}
                placeholder="Sélectionner un rôle"
                style={{ width: '100%' }}
              />
            </FormItem>
          </Col>
          <Col
            md={12}
            sm={24}
            style={{
              display: this.state.displayShowSelect ? 'block' : 'none',
            }}
          >
            <FormItem label="Salon(s)" name="shows" required={false}>
              <Select
                mode="multiple"
                onDeselect={async (value?: string) => {
                  const userShow = this.collUserShow.find((us: UserShowModel) => us.showIri === value);
                  if (userShow) {
                    try {
                      await userShow.destroy();
                    } catch (error) {
                      handleApiError(error);
                    }
                  }
                }}
                placeholder={<FormattedMessageWrapper id="Sélectionner le(s) salon(s)" />}
                style={{ width: '100%' }}
              >
                {this.collShow.map((show: ShowModel) => (
                  <Select.Option key={show.id} value={show.iri}>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                      <Avatar
                        shape="square"
                        size={16}
                        src={show.logoObjectUrl}
                        style={{ marginRight: 6 }}
                      />
                      {show.showName}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={24}>
            <FormItem label="Mot de passe" name="plainPassword" required={false}>
              <InputPassword placeholder="Mot de passe" />
            </FormItem>
          </Col>
          <Col md={12} sm={24}>
            <FormItem
              label="Confirmation du mot de passe"
              name="plainPasswordConfirmation"
              required={false}
              rules={[
                (form: FormInstance) => ({
                  message: <FormattedMessageWrapper id="Confirmation mot de passe incorrecte !" />,
                  validator: (_: any, value?: string) => {
                    if (value && value !== form?.getFieldValue('plainPassword')) {
                      return Promise.reject();
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputPassword placeholder="Confirmation du mot de passe" />
            </FormItem>
          </Col>
        </Row>
      </Form>
    );
  }

  public submit = async () => {
    const { model, onSuccess } = this.props;

    const values = await this.formRef.current?.validateFields();

    try {
      const finalValues = {
        company: values.company,
        email: values.email,
        enabled: values.enabled,
        firstName: values.firstName,
        lastName: values.lastName,
        plainPassword: values.plainPassword ? values.plainPassword : undefined,
        role: values.role,
        shows: values.shows,
      };

      await model.patch(finalValues);

      for (const showIri of values.shows) {
        let userShow = this.collUserShow.findBy('show.@id', showIri);
        if (!userShow) {
          userShow = new UserShowModel();
          userShow.set({
            show: showIri,
            user: model.iri,
          });
          await userShow.save();
        }
      }

      messageSuccessIntl("Enregistrement réussi !");

      if (onSuccess) {
        onSuccess();
      }

      this.formRef.current?.resetFields();
    } catch (error) {
      handleFormError(error, this.formRef.current);
    }
  };

  private setInitialValues = async () => {
    const { model } = this.props;

    this.collUserShow.clear();

    if (model.id) {
      this.collUserShow.setFilter('user', model.id);
      await this.collUserShow.list();
    }

    const values = {
      company: model.company,
      email: model.email,
      enabled: model.enabled || false,
      firstName: model.firstName,
      lastName: model.lastName,
      phone: model.phone,
      role: model.roleIri,
      shows: this.collUserShow.map((userShow: UserShowModel) => userShow.showIri),
    };

    this.formRef.current?.setFieldsValue(values);

    const role = this.collRoleBack.findBy('@id', model.roleIri) as RoleModel;


    this.setState({
      displayShowSelect: rolesBackWithShows.includes(role?.reference),
    });
  };
}

// const AdministratorFormObserver = observer(forwardRef((props: any, ref) => {
//   const instanceRef = React.useRef<AdministratorForm>(null);
//
//   // On expose la méthode submit à la ref parente (celle de AppForm)
//   useImperativeHandle(ref, () => ({
//     submit: () => instanceRef.current?.submit()
//   }));
//
//   return <AdministratorForm {...props} ref={instanceRef} />;
// }));

export default AdministratorForm;
